/**
 * template-analytics.ts — GA4 tracking for the guest-facing invitation templates, done ONCE here so
 * every current and future template gets it without per-template code.
 *
 * Surfaces: real invitations (`/i/:slug`, context set by PublicInvitation once loaded) and the static
 * template demos (`/demo/<name>`). Mounted globally by <GuestTemplateTracker/> in App.tsx.
 *
 * Privacy (DPDP): guests are not account holders. Events carry ONLY template_id, occasion_type,
 * invitation_id (the database UUID — never the public slug, which is built from the hosts' names),
 * section, status, element_id and scroll_percent. `sanitizeTemplateParams` enforces that allow-list,
 * so a future caller cannot leak a name/phone/email by accident. Nothing a guest types is read.
 *
 * Everything no-ops when GA4 is off (no VITE_GA_MEASUREMENT_ID, non-allowed host, tests) and never
 * throws: analytics must not break an invitation.
 */
import { isAnalyticsActive, restoreGuestPageContext, trackEvent, trackTemplatePageView, type GaParams } from './analytics';

// ── Context ────────────────────────────────────────────────────────────────

export type TemplateSurface = 'invitation' | 'demo';

export interface TemplateContext {
  templateId?: string | null;
  occasionType?: string | null;
  invitationId?: string | null;
  surface: TemplateSurface;
}

let current: TemplateContext | null = null;

/** Allowed GA param keys for template events. Anything else is dropped. */
export const TEMPLATE_PARAM_ALLOW_LIST = [
  'template_id', 'occasion_type', 'invitation_id', 'section', 'status', 'element_id', 'scroll_percent',
] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_TOKEN = /^[A-Za-z0-9_.:-]{1,100}$/;
const EMAIL_OR_PHONE = /@|\+?\d[\d\s-]{7,}\d/;

/** Keep only allow-listed keys with safe, bounded, non-PII-looking values. */
export function sanitizeTemplateParams(params: Record<string, unknown>): GaParams {
  const out: Record<string, string | number> = {};
  for (const key of TEMPLATE_PARAM_ALLOW_LIST) {
    const v = params[key];
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'number') {
      if (Number.isFinite(v)) out[key] = v;
      continue;
    }
    if (typeof v !== 'string') continue;
    if (key === 'invitation_id') {
      // Must be the DB UUID (never a slug). A valid UUID skips the phone heuristic, whose digit-run
      // pattern otherwise matches UUIDs like 1111…-4333-8444-5555… and silently drops them.
      if (UUID.test(v)) out[key] = v;
      continue;
    }
    if (!SAFE_TOKEN.test(v) || EMAIL_OR_PHONE.test(v)) continue;
    out[key] = v;
  }
  return out as GaParams;
}

function contextParams(ctx: TemplateContext | null = current): GaParams {
  if (!ctx) return {};
  return sanitizeTemplateParams({
    template_id: ctx.templateId ?? undefined,
    occasion_type: ctx.occasionType ?? undefined,
    invitation_id: ctx.invitationId ?? undefined,
    section: ctx.surface,
  });
}

/** Params of the template currently on screen ({} when none) — for api.ts guest events. */
export function templateContextParams(): GaParams {
  return contextParams();
}

export function getTemplateContext(): TemplateContext | null {
  return current;
}

export function clearTemplateContext(pathname?: string): void {
  current = null;
  viewedKey = null;
  restoreGuestPageContext(pathname);
}

// ── Routes ─────────────────────────────────────────────────────────────────

/** Demo routes → the template id they showcase. Unknown demos fall back to `demo-<name>`. */
export const DEMO_TEMPLATE_IDS: Record<string, string> = {
  'wedding-plot-plane': 'exp-wedding-plot-plane-3d',
  'royal-mandap-palace': 'royal-mandap-palace',
  'marigold-haveli-journey': 'marigold-haveli-journey',
  'lake-palace-destination': 'lake-palace-destination',
  'indo-ghibli-wedding': 'exp-wedding-indo-ghibli-v1',
  'pichwai-heritage-wedding': 'exp-wedding-pichwai-v1',
  'storybook-3d-wedding': 'exp-wedding-storybook-3d-v1',
};

/** `/i/:slug` or `/demo/<name>` (not the bare `/demo` gallery). */
export function guestTemplateRoute(pathname: string): { surface: TemplateSurface; redactedPath: string; demo?: string } | null {
  const p = pathname.replace(/\/+$/, '');
  if (/^\/i\/[^/]+$/.test(p)) return { surface: 'invitation', redactedPath: '/i/:slug' };
  const m = /^\/demo\/([a-z0-9-]+)$/.exec(p);
  if (m) return { surface: 'demo', redactedPath: p, demo: m[1] };
  return null;
}

export function isGuestTemplatePath(pathname: string): boolean {
  return guestTemplateRoute(pathname) !== null;
}

// ── Page view ──────────────────────────────────────────────────────────────

let viewedKey: string | null = null;

/**
 * Record the template on screen and send its (single) page_view. De-duplicated per
 * path+invitation, so StrictMode double effects and re-renders don't double count.
 */
export function startTemplateView(pathname: string, ctx: TemplateContext, status?: string): void {
  const route = guestTemplateRoute(pathname);
  if (!route) return;
  current = ctx;
  resetInteractionState();
  const key = `${pathname}|${ctx.invitationId ?? ''}|${status ?? ''}`;
  if (viewedKey === key) return;
  viewedKey = key;
  trackTemplatePageView(route.redactedPath, { ...contextParams(ctx), ...sanitizeTemplateParams({ status }) });
}

/** Test-only. */
export function __resetTemplateAnalytics(): void {
  current = null;
  viewedKey = null;
  resetInteractionState();
}

// ── Guest interactions ─────────────────────────────────────────────────────

const SCROLL_MILESTONES = [25, 50, 75, 100]; // same steps as main's SCROLL_DEPTH
let scrollFired = new Set<number>();
let musicFired = false;

function resetInteractionState(): void {
  scrollFired = new Set();
  musicFired = false;
}

function fire(name: Parameters<typeof trackEvent>[0], extra: Record<string, unknown> = {}): void {
  try {
    trackEvent(name, 'guest', undefined, undefined, { ...contextParams(), ...sanitizeTemplateParams(extra) });
  } catch {
    /* never break the invitation */
  }
}

/** Classify a click target: share (WhatsApp / native / copy link) or directions (maps). */
export function classifyGuestClick(target: Element): { kind: 'share' | 'directions'; via: string } | null {
  const el = target.closest('a,button,[role="button"],[data-track]');
  if (!el) return null;
  const href = (el.getAttribute('href') || '').toLowerCase();
  if (/(^|\/\/)(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)|^whatsapp:/.test(href)) return { kind: 'share', via: 'whatsapp' };
  if (/google\.[a-z.]+\/maps|maps\.google\.|maps\.app\.goo\.gl|goo\.gl\/maps/.test(href)) return { kind: 'directions', via: 'google-maps' };
  if (/maps\.apple\.com/.test(href)) return { kind: 'directions', via: 'apple-maps' };
  if (/^geo:/.test(href)) return { kind: 'directions', via: 'geo' };
  const label = [el.getAttribute('data-track'), el.getAttribute('aria-label'), el.getAttribute('title'), (el.textContent || '').slice(0, 60)]
    .filter(Boolean).join(' ').toLowerCase();
  if (/whatsapp/.test(label)) return { kind: 'share', via: 'whatsapp' };
  if (/\b(share|copy link)\b/.test(label)) return { kind: 'share', via: 'share' };
  if (/\b(directions|get directions|view map|open map|navigate)\b/.test(label)) return { kind: 'directions', via: 'map-button' };
  return null;
}

export function scrollPercent(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  const max = scrollHeight - clientHeight;
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((scrollTop / max) * 100)));
}

/** Fire newly-reached milestones for a scroll percent. Returns those fired (for tests). */
export function noteScroll(percent: number): number[] {
  const hit = SCROLL_MILESTONES.filter((m) => percent >= m && !scrollFired.has(m));
  for (const m of hit) {
    scrollFired.add(m);
    fire('SCROLL_DEPTH', { scroll_percent: m });
  }
  return hit;
}

function noteMusic(el: unknown): void {
  if (musicFired || !(el instanceof HTMLAudioElement)) return;
  musicFired = true;
  fire('GUEST_MUSIC_PLAYED');
}

/**
 * Install delegated listeners for the current guest page. Returns an uninstall function.
 * Costs nothing when GA4 is off (returns a no-op).
 */
export function installGuestTemplateTracking(): () => void {
  if (typeof window === 'undefined' || !isAnalyticsActive()) return () => {};

  const onClick = (e: MouseEvent) => {
    if (!(e.target instanceof Element) || e.target.closest('[data-ga-ignore]')) return;
    const c = classifyGuestClick(e.target);
    if (!c) return;
    fire(c.kind === 'share' ? 'GUEST_SHARE_CLICKED' : 'GUEST_DIRECTIONS_CLICKED', { element_id: c.via });
  };

  let raf = 0;
  const onScroll = (e: Event) => {
    if (raf) return;
    const t = e.target;
    raf = window.requestAnimationFrame(() => {
      raf = 0;
      const doc = document.scrollingElement || document.documentElement;
      let pct = scrollPercent(window.scrollY || doc.scrollTop, doc.scrollHeight, window.innerHeight);
      // Many templates scroll an inner full-screen container, not the window.
      if (t instanceof HTMLElement && t.clientHeight > window.innerHeight * 0.6) {
        pct = Math.max(pct, scrollPercent(t.scrollTop, t.scrollHeight, t.clientHeight));
      }
      noteScroll(pct);
    });
  };

  // Media `play` doesn't bubble but is seen in the capture phase for <audio> in the DOM.
  const onPlay = (e: Event) => noteMusic(e.target);
  // Templates also play detached `new Audio(src)` objects; wrap play() while a guest page is open.
  const proto = HTMLMediaElement.prototype;
  const originalPlay = proto.play;
  const wrappedPlay = function (this: HTMLMediaElement) {
    // eslint-disable-next-line prefer-rest-params
    const result = originalPlay.apply(this, arguments as unknown as []);
    try {
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).then(() => noteMusic(this), () => {});
      } else {
        noteMusic(this);
      }
    } catch {
      /* ignore */
    }
    return result;
  };
  try {
    proto.play = wrappedPlay;
  } catch {
    /* non-writable in some environments */
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  document.addEventListener('play', onPlay, true);

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
    document.removeEventListener('play', onPlay, true);
    if (raf) window.cancelAnimationFrame(raf);
    try {
      if (proto.play === wrappedPlay) proto.play = originalPlay;
    } catch {
      /* ignore */
    }
  };
}
